import constants from '../../../../settings/constants';
import tacHelpers from '../../tacHelpers';
import { isType, isExtendable } from '../../../../services/products';
import { config } from '../../../../scene/jonaxel/ClothesRailConfig';
import productService from '../../../../services/products';
import { isStandAlone } from '../../../../services/products/models';
import { ITEMS } from '../../../../constants';

function isBelowParent(slot) {
  return slot.y < slot.parent.y;
}

function isBlockedByClothesRail(slot, tac) {
  function isBlockingClothesRail(item, slot) {
    return (
      isType(item, ITEMS.CLOTHES_RAIL) &&
      isExtendable(item) &&
      ((isType(slot, 'leg') && slot.height > constants.PAD_HEIGHT) ||
        (isType(slot, 'cover') &&
          item.y < slot.parent.height - constants.DISTANCE_BETWEEN_ATTACHMENTS))
    );
  }

  return (
    (slot.parent.items &&
      slot.parent.items.find(item => isBlockingClothesRail(item, slot))) ||
    tacHelpers
      .getAllItems(tac.items)
      .find(
        item =>
          item.connectsTo &&
          item.connectsTo.itemid === slot.parent.itemid &&
          isBlockingClothesRail(item, slot)
      )
  );
}

function onWheels(tac, item) {
  return tacHelpers
    .getTopAncestor(tac, item)
    .items?.some(
      item => productService.isLeg(item) && item.filter.variant === 'castors'
    );
}

/**
 * Decides whether a given slot is to be considered valid or not
 * based on a number of rules.
 * @param {*} slot The slot to validate.
 * @param {*} tac The TAC.
 * @returns {boolean} The validness of the slot.
 */
function valid(slot, tac) {
  // a stack is invalid if it's three or more parts tall
  // so either the parent is more than one part tall.
  // Protective pads should not be counted as a part in this case.
  if (
    productService.isStackable(slot.parent) &&
    productService.isStackable(slot) &&
    slot.parent.y > constants.PAD_HEIGHT
  ) {
    return false;
  }
  //.. or the fitted stack is
  if (
    productService.isStackable(slot) &&
    (onWheels(tac, slot) || (slot.items || []).some(productService.isStackable))
  ) {
    return false;
  }

  // Stacking on top of a covered parent is also invalid
  if (
    productService.isStackable(slot) &&
    slot.parent.items?.some(item => productService.isType(item, 'cover'))
  ) {
    return false;
  }

  //.. or we are trying to place an item below an already stacked parent,
  // and the item is not listed as a part on the parent
  if (
    isBelowParent(slot) &&
    productService.isStackable(slot.parent) &&
    (slot.parent.items || []).some(productService.isStackable) &&
    !(Object.values(slot.parent.parts) || []).some(part => part === slot.id)
  ) {
    return false;
  }
  // Covers and legs are possibly blocked by extendable clothes rail depending on pos.
  if (isType(slot, ['cover', 'leg']) && isBlockedByClothesRail(slot, tac)) {
    return false;
  }

  // It should not be possible to add another cover if a cover is already present.
  if (
    isType(slot, ['cover']) &&
    slot.parent.items?.some(item => productService.isType(item, 'cover'))
  ) {
    return false;
  }
  return true;
}

function filterSlots(slots, tac) {
  return slots.filter(slot => valid(slot, tac));
}

function hasBlockingCover(tac, slot) {
  return (
    slot.local &&
    slot.local.y <
      slot.parent.height - constants.DISTANCE_BETWEEN_ATTACHMENTS &&
    slot.parent.items &&
    slot.parent.items.find(item => isType(item, 'cover'))
  );
}

/*
If either the left or the right frame is connected to a clothes rail with a different partner,
the slot is deemed invalid.
*/
function hasConflictingClothesRail(left, right, tac) {
  const existingExtCrs = tacHelpers
    .getClothesRails(tac)
    .filter(cr => productService.isExtendable(cr));

  if (!existingExtCrs.length) {
    return false;
  }

  const topAncestorLeft = tacHelpers.getTopAncestor(tac, left.parent);
  const topAncestorRight = tacHelpers.getTopAncestor(tac, right.parent);

  return existingExtCrs.find(clothesRail => {
    const clothesRailTopAncestor = tacHelpers.getTopAncestor(tac, clothesRail);
    const connectsToTopAncestor =
      clothesRail.connectsTo &&
      tacHelpers.getTopAncestor(
        tac,
        tacHelpers.getItem(tac, clothesRail.connectsTo.itemid)
      );
    return (
      connectsToTopAncestor &&
      ((clothesRailTopAncestor.itemid === topAncestorLeft.itemid &&
        connectsToTopAncestor.itemid !== topAncestorRight.itemid) ||
        (clothesRailTopAncestor.itemid !== topAncestorLeft.itemid &&
          connectsToTopAncestor.itemid === topAncestorRight.itemid))
    );
  });
}

function getPartnerConfig(partner) {
  return config;
}

function partnerSlots(slots, otherSlots, tac) {
  // add complement slot for other half of extendable clothes rod
  const insideSlots = slots
    .filter(slot => slot.id === config.innerId)
    .filter(
      slot => !onWheels(tac, slot.parent) && !hasBlockingCover(tac, slot)
    );
  if (insideSlots.length) {
    const outsideSlots = otherSlots.filter(
      slot => !onWheels(tac, slot.parent) && !hasBlockingCover(tac, slot)
    );
    insideSlots.forEach(left => {
      const fits = outsideSlots.filter(
        right =>
          right.x > left.x &&
          right.y === left.y &&
          right.x + right.width <= left.x + config.maxWidth &&
          !hasConflictingClothesRail(left, right, tac)
      );
      if (fits.length === 1) {
        left.partnerSlot = fits[0];
      }
      if (fits.length > 1) {
        throw new Error('found more than one fit for outside clothes rail');
      }
    });
  }
  return slots.filter(slot => slot.id !== config.innerId || slot.partnerSlot);
}

function getDependentItems(item, tac) {
  //Not implemented for jonaxel
  return [];
}

function getProppingItemsToAdapt(tac, movingItem) {
  if (!movingItem || !isStandAlone(movingItem)) {
    return [];
  }

  const connectedClothesRails = [
    ...tacHelpers
      .getClothesRails({ items: [movingItem] })
      .filter(cr => productService.isExtendable(cr)),
    ...tacHelpers.getConnectedClothesRails(tac, movingItem),
  ];

  const otherExtCrs = tacHelpers
    .getClothesRails(tac)
    .filter(
      cr =>
        productService.isExtendable(cr) &&
        !connectedClothesRails.some(connected => connected.itemid === cr.itemid)
    );

  return otherExtCrs;
}

function getSlotSources(item, tac) {
  //Not implemented for jonaxel
  return [];
}

function moveWithCr(item) {
  return tacHelpers.hasExtClothesRail(item);
}

export default {
  filterSlots,
  getDependentItems,
  getPartnerConfig,
  getProppingItemsToAdapt,
  getSlotSources,
  moveWithCr,
  partnerSlots,
};
